// Sophia's live thought stream.
//
// A tiny local pub/sub for the things she is turning over *right now* —
// recalls, plans, worries, world observations. Everything is on-device: the
// last few dozen thoughts are kept in localStorage so a reload still shows the
// train of thought, and nothing is ever uploaded.

export type ThoughtKind = "recall" | "plan" | "watch" | "feel" | "world" | "speak" | "learn";

export interface Thought {
  id: string;
  at: number;
  kind: ThoughtKind;
  text: string;
}

const KEY = "sophia:thoughts:v1";
const MAX = 60;

let buffer: Thought[] = load();
const listeners = new Set<(t: Thought[]) => void>();

function load(): Thought[] {
  try {
    const raw = localStorage.getItem(KEY);
    const list = raw ? (JSON.parse(raw) as Thought[]) : [];
    return Array.isArray(list) ? list.slice(-MAX) : [];
  } catch {
    return [];
  }
}

function persist(): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(buffer.slice(-MAX)));
  } catch {
    /* storage full or blocked — thoughts still stream in memory */
  }
}

function emit(): void {
  const snap = buffer.slice();
  for (const l of listeners) {
    try {
      l(snap);
    } catch {
      /* a broken caption box must never stop her thinking */
    }
  }
}

/** Push one thought. Duplicates in a row are folded so the box stays readable. */
export function pushThought(text: string, kind: ThoughtKind = "plan"): void {
  const clean = (text || "").replace(/\s+/g, " ").trim();
  if (!clean) return;
  const last = buffer[buffer.length - 1];
  if (last && last.text === clean && last.kind === kind) return;
  buffer = [
    ...buffer,
    {
      id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
      at: Date.now(),
      kind,
      text: clean,
    },
  ].slice(-MAX);
  persist();
  emit();
}

export function thoughts(limit = MAX): Thought[] {
  return buffer.slice(-Math.max(1, limit));
}

export function latestThought(): Thought | null {
  return buffer[buffer.length - 1] ?? null;
}

export function subscribeThoughts(fn: (t: Thought[]) => void): () => void {
  listeners.add(fn);
  try {
    fn(buffer.slice());
  } catch {
    /* ignore */
  }
  return () => {
    listeners.delete(fn);
  };
}

export function clearThoughts(): void {
  buffer = [];
  persist();
  emit();
}

export const THOUGHT_LABEL: Record<ThoughtKind, string> = {
  recall: "recalling",
  plan: "working it out",
  watch: "watching",
  feel: "feeling",
  world: "in the world",
  speak: "about to say",
  learn: "learning",
};
